import { ApolloProvider } from '@apollo/client';
import { SynopticLogoIcon } from '@synoptic/ui-kit/icons/react/synoptic-logo.tsx';
import { ThemeProvider } from '@synoptic/ui-kit/theme/theme-provider.js';
import { lightTheme } from '@synoptic/ui-kit/theme/theme.css.js';
import { clsx } from 'clsx';
import { ReactNode, useEffect } from 'react';
import {
  ClientLoaderFunction,
  href,
  matchPath,
  Outlet,
  redirect,
  UIMatch,
  useMatches,
} from 'react-router';
import { getApolloClient } from '@/apollo/apollo-client-instance.ts';
import { Footer } from '@/components/footer/footer.tsx';
import { getStaticCacheControl } from '@/routes-utils/get-static-cache-control.ts';
import { paths } from '@/routes-utils/paths';
import { sanitizeRedirectParam } from '@/routes-utils/sanitize-redirect-param.ts';
import { appSessionClientMiddleware } from '@/session-middleware.ts';
import { sessionContext } from '@/session.ts';
import { SplashScreen } from '../_app/splash-screen.tsx';
import { loginStepVar } from '../_auth.auth.login/login-step-var.ts';
import { resetPasswordStepVar } from '../_auth.auth.reset-password/reset-password-step-var.ts';
import { signupStepVar } from '../_auth.auth.signup/signup-step-var.ts';
import { Brand } from './brand.tsx';
import {
  footerContainer,
  header,
  welcomeContainer,
  welcomeGradient,
} from './page.css.ts';

export const headers = () => getStaticCacheControl();

const redirectIfIndex = ({ request }: { request: Request }) => {
  const isIndex = matchPath(href('/auth'), new URL(request.url).pathname);

  if (!isIndex) return null;

  throw redirect(paths.login);
};

export const clientMiddleware = [appSessionClientMiddleware];

export const clientLoader: ClientLoaderFunction = async ({
  request,
  context,
}) => {
  redirectIfIndex({ request });

  const pathname = new URL(request.url).pathname;
  const params = new URL(request.url).searchParams;
  const fromParam = params.get('from');
  const from = sanitizeRedirectParam(fromParam);
  const session = context.get(sessionContext);

  switch (session.kind) {
    case 'guest':
      return null;
    case 'incomplete':
      signupStepVar('chooseUsername');
      if (pathname !== paths.signup) {
        return redirect(paths.signup);
      }
      return null;
    case 'user':
      return redirect(from);
    default: {
      const _exhaustive: never = session;
      return _exhaustive;
    }
  }
};
clientLoader.hydrate = true;

export const loader = redirectIfIndex;

export const HydrateFallback = () => <SplashScreen forcedLightMode />;

export default function Auth() {
  const matches = useMatches() as UIMatch<
    unknown,
    {
      brandContent?: () => ReactNode;
    }
  >[];

  const BrandContent = matches
    .find((m) => m.handle?.brandContent)
    ?.handle?.brandContent?.() ?? <Brand />;

  useEffect(() => {
    return () => {
      loginStepVar('login');
      signupStepVar('signup');
      resetPasswordStepVar('resetPassword');
    };
  }, []);

  return (
    <ApolloProvider client={getApolloClient()}>
      <ThemeProvider mode="light">
        <div className={clsx(welcomeGradient, lightTheme)}>
          <div className={welcomeContainer}>
            <div className={header}>
              <SynopticLogoIcon />
            </div>

            {BrandContent}
            <Outlet />

            <Footer className={footerContainer} />
          </div>
        </div>
      </ThemeProvider>
    </ApolloProvider>
  );
}
